import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import {
	getJourneyAdmin, createJourney, updateJourney, duplicateJourney,
} from '../../../api/contentAdminApi';
import { JOURNEY_REGIONS } from '../../../data/cmsAdminConfig';
import { CmsEditorialFields, CmsFormShell } from './CmsFormFields';
import AvailableMonthsPicker from './AvailableMonthsPicker';
import '../../../styles/admin-cms.scss';

const EMPTY = {
	slug: '', title: '', region: JOURNEY_REGIONS[0], district: '', summary: '', highlight: '',
	duration_label: '', max_altitude: '', difficulty_label: '', budget_label: '', seasons_label: '',
	hero_image_url: '', coverage_percent: 0, restricted_area: false, handcrafted: false,
	priority: 0, featured: false,
	editorial_status: 'draft', sections: {}, guided_content: {},
	booking_defaults: {
		vendor_total_price: '',
		currency_code: 'USD',
		duration_days: '',
		origin: 'Kathmandu',
		destination: '',
		min_group_size: 6,
		max_group_size: 12,
		group_enabled: true,
		private_enabled: true,
		price_type: 'per_person',
		group_markup_percentage: '',
		group_interest_percentage: '',
		private_markup_percentage: '',
		private_interest_percentage: '',
		available_months: [],
	},
};

const EMPTY_BOOKING = EMPTY.booking_defaults;

function optionalNumber(value) {
	return value !== '' && value != null ? Number(value) : null;
}

function formatOptionalNumber(value) {
	return value != null && value !== '' ? String(value) : '';
}

export default function JourneyAdminForm() {
	const { slug } = useParams();
	const history = useHistory();
	const isEdit = Boolean(slug);
	const [form, setForm] = useState(EMPTY);
	const [sectionsJson, setSectionsJson] = useState('{}');
	const [guidedJson, setGuidedJson] = useState('{}');
	const [booking, setBooking] = useState(EMPTY_BOOKING);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isEdit) return;
		getJourneyAdmin(slug)
			.then((res) => {
				const j = res.data?.journey || res.data;
				setForm({ ...EMPTY, ...j });
				setSectionsJson(JSON.stringify(j.sections || {}, null, 2));
				setGuidedJson(JSON.stringify(j.guided_content || {}, null, 2));
				setBooking({
					...EMPTY_BOOKING,
					...(j.booking_defaults || {}),
					group_markup_percentage: formatOptionalNumber(j.booking_defaults?.group_markup_percentage),
					group_interest_percentage: formatOptionalNumber(j.booking_defaults?.group_interest_percentage),
					private_markup_percentage: formatOptionalNumber(j.booking_defaults?.private_markup_percentage),
					private_interest_percentage: formatOptionalNumber(j.booking_defaults?.private_interest_percentage),
					available_months: Array.isArray(j.booking_defaults?.available_months) ? j.booking_defaults.available_months : [],
				});
			})
			.catch(() => swal('Error', 'Could not load journey', 'error'));
	}, [isEdit, slug]);

	const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
	const onBookingChange = (key, value) => setBooking((b) => ({ ...b, [key]: value }));

	const handleSubmit = async (e) => {
		e.preventDefault();
		let sections = {};
		let guided_content = {};
		try {
			sections = JSON.parse(sectionsJson || '{}');
			guided_content = JSON.parse(guidedJson || '{}');
		} catch {
			swal('Invalid JSON', 'Check sections or guided content JSON.', 'error');
			return;
		}
		setSaving(true);
		const booking_defaults = {
			...booking,
			vendor_total_price: booking.vendor_total_price ? Number(booking.vendor_total_price) : null,
			duration_days: booking.duration_days ? Number(booking.duration_days) : null,
			min_group_size: Number(booking.min_group_size) || 6,
			max_group_size: Number(booking.max_group_size) || 12,
			group_markup_percentage: optionalNumber(booking.group_markup_percentage),
			group_interest_percentage: optionalNumber(booking.group_interest_percentage),
			private_markup_percentage: optionalNumber(booking.private_markup_percentage),
			private_interest_percentage: optionalNumber(booking.private_interest_percentage),
			available_months: Array.isArray(booking.available_months) && booking.available_months.length
				? booking.available_months.map(Number)
				: [],
		};
		const payload = {
			...form,
			sections,
			guided_content,
			booking_defaults,
			coverage_percent: Number(form.coverage_percent) || 0,
			priority: Number(form.priority) || 0,
			featured: !!form.featured,
			published: !!form.published,
		};
		try {
			if (isEdit) await updateJourney(slug, payload);
			else await createJourney(payload);
			swal('Saved', 'Journey saved successfully.', 'success');
			history.push('/admin/cms/journeys');
		} catch (err) {
			swal('Error', err.response?.data?.errors?.[0] || 'Could not save', 'error');
		} finally {
			setSaving(false);
		}
	};

	return (
		<CmsFormShell title={isEdit ? `Edit journey: ${form.title}` : 'New journey'} backHref="/admin/cms/journeys" onSubmit={handleSubmit} saving={saving}>
			<fieldset className="admin-cms-form__section">
				<legend>Basics</legend>
				<div className="admin-cms-form__row">
					<label><span>Title</span><input required value={form.title} onChange={(e) => onChange('title', e.target.value)} /></label>
					<label><span>Slug</span><input required value={form.slug} onChange={(e) => onChange('slug', e.target.value)} disabled={isEdit} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label>
						<span>Region</span>
						<select value={form.region} onChange={(e) => onChange('region', e.target.value)}>
							{JOURNEY_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
						</select>
					</label>
					<label><span>District</span><input value={form.district || ''} onChange={(e) => onChange('district', e.target.value)} /></label>
				</div>
				<label><span>Summary</span><textarea rows={3} value={form.summary || ''} onChange={(e) => onChange('summary', e.target.value)} /></label>
				<label><span>Highlight</span><input value={form.highlight || ''} onChange={(e) => onChange('highlight', e.target.value)} /></label>
				<div className="admin-cms-form__row">
					<label><span>Duration</span><input value={form.duration_label || ''} onChange={(e) => onChange('duration_label', e.target.value)} /></label>
					<label><span>Max altitude</span><input value={form.max_altitude || ''} onChange={(e) => onChange('max_altitude', e.target.value)} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Difficulty</span><input value={form.difficulty_label || ''} onChange={(e) => onChange('difficulty_label', e.target.value)} /></label>
					<label><span>Budget</span><input value={form.budget_label || ''} onChange={(e) => onChange('budget_label', e.target.value)} /></label>
				</div>
				<label><span>Best seasons</span><input value={form.seasons_label || ''} onChange={(e) => onChange('seasons_label', e.target.value)} /></label>
				<label><span>Hero image URL</span><input value={form.hero_image_url || ''} onChange={(e) => onChange('hero_image_url', e.target.value)} /></label>
				<div className="admin-cms-form__row">
					<label><span>Coverage %</span><input type="number" min="0" max="100" value={form.coverage_percent || 0} onChange={(e) => onChange('coverage_percent', e.target.value)} /></label>
					<label className="admin-cms-form__check"><input type="checkbox" checked={!!form.restricted_area} onChange={(e) => onChange('restricted_area', e.target.checked)} /> Restricted area</label>
					<label className="admin-cms-form__check"><input type="checkbox" checked={!!form.handcrafted} onChange={(e) => onChange('handcrafted', e.target.checked)} /> Handcrafted (premium content)</label>
				</div>
			</fieldset>
			<fieldset className="admin-cms-form__section">
				<legend>Homepage & listing order</legend>
				<p className="admin-cms-form__hint">Lower priority numbers appear first. Featured journeys show on the homepage spotlight and featured panel.</p>
				<div className="admin-cms-form__row">
					<label>
						<span>Priority</span>
						<input type="number" min="0" step="1" value={form.priority ?? 0} onChange={(e) => onChange('priority', e.target.value)} />
					</label>
					<label className="admin-cms-form__check">
						<input type="checkbox" checked={!!form.featured} onChange={(e) => onChange('featured', e.target.checked)} />
						Featured on homepage
					</label>
					<label className="admin-cms-form__check">
						<input type="checkbox" checked={!!form.published} onChange={(e) => onChange('published', e.target.checked)} />
						Published (visible to visitors)
					</label>
				</div>
			</fieldset>
			<fieldset className="admin-cms-form__section">
				<legend>Booking defaults</legend>
				<p className="admin-cms-form__hint">
					Group/private dropdowns and live pricing use the{' '}
					<Link to="/admin/booking_catalog">Booking catalog</Link>
					{' '}— not this form. Fields here are legacy fallbacks only if no catalog entry exists for this slug.
				</p>
				<div className="admin-cms-form__row">
					<label>
						<span>{booking.price_type === 'total' ? 'Vendor base price (total trip)' : 'Vendor base price (per person)'}</span>
						<input type="number" min="0" step="1" value={booking.vendor_total_price || ''} onChange={(e) => onBookingChange('vendor_total_price', e.target.value)} />
					</label>
					<label>
						<span>Currency</span>
						<select value={booking.currency_code || 'USD'} onChange={(e) => onBookingChange('currency_code', e.target.value)}>
							<option value="USD">USD</option>
							<option value="NPR">NPR</option>
						</select>
					</label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Duration (days)</span><input type="number" min="1" value={booking.duration_days || ''} onChange={(e) => onBookingChange('duration_days', e.target.value)} /></label>
					<label><span>Price type</span>
						<select value={booking.price_type || 'per_person'} onChange={(e) => onBookingChange('price_type', e.target.value)}>
							<option value="per_person">Per person</option>
							<option value="total">Total trip</option>
						</select>
					</label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Group markup %</span><input type="number" min="0" max="100" step="0.01" placeholder="Platform default" value={booking.group_markup_percentage || ''} onChange={(e) => onBookingChange('group_markup_percentage', e.target.value)} /></label>
					<label><span>Group interest %</span><input type="number" min="0" max="100" step="0.01" placeholder="Platform default" value={booking.group_interest_percentage || ''} onChange={(e) => onBookingChange('group_interest_percentage', e.target.value)} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Private markup %</span><input type="number" min="0" max="100" step="0.01" placeholder="Platform default" value={booking.private_markup_percentage || ''} onChange={(e) => onBookingChange('private_markup_percentage', e.target.value)} /></label>
					<label><span>Private interest %</span><input type="number" min="0" max="100" step="0.01" placeholder="Platform default" value={booking.private_interest_percentage || ''} onChange={(e) => onBookingChange('private_interest_percentage', e.target.value)} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Origin</span><input value={booking.origin || ''} onChange={(e) => onBookingChange('origin', e.target.value)} /></label>
					<label><span>Destination</span><input value={booking.destination || ''} onChange={(e) => onBookingChange('destination', e.target.value)} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label><span>Min group size</span><input type="number" min="1" value={booking.min_group_size || 6} onChange={(e) => onBookingChange('min_group_size', e.target.value)} /></label>
					<label><span>Max group size</span><input type="number" min="1" value={booking.max_group_size || 12} onChange={(e) => onBookingChange('max_group_size', e.target.value)} /></label>
				</div>
				<div className="admin-cms-form__row">
					<label className="admin-cms-form__check"><input type="checkbox" checked={booking.group_enabled !== false} onChange={(e) => onBookingChange('group_enabled', e.target.checked)} /> Group departures enabled</label>
					<label className="admin-cms-form__check"><input type="checkbox" checked={booking.private_enabled !== false} onChange={(e) => onBookingChange('private_enabled', e.target.checked)} /> Private tours enabled</label>
				</div>
				<fieldset className="admin-cms-form__subsection">
					<legend>Available months</legend>
					<AvailableMonthsPicker
						value={booking.available_months || []}
						onChange={(months) => onBookingChange('available_months', months)}
					/>
				</fieldset>
			</fieldset>
			<fieldset className="admin-cms-form__section">
				<legend>Sections (JSON)</legend>
				<p className="admin-cms-form__hint">Overview, difficulty, packing, permits, FAQs, maps, weather, gallery — structured JSON until section editors ship.</p>
				<textarea className="admin-cms-form__code" rows={10} value={sectionsJson} onChange={(e) => setSectionsJson(e.target.value)} />
			</fieldset>
			<fieldset className="admin-cms-form__section">
				<legend>Guided content (JSON)</legend>
				<textarea className="admin-cms-form__code" rows={8} value={guidedJson} onChange={(e) => setGuidedJson(e.target.value)} />
			</fieldset>
			<CmsEditorialFields form={form} onChange={onChange} />
		</CmsFormShell>
	);
}
